/**
 * Runtime configuration for the web app.
 *
 * Vite inlines `import.meta.env.VITE_*` values at build time, which forces one
 * build per environment. The Docker image ships a generic build instead: the
 * container entrypoint (frontend/docker/entrypoint.sh) writes `/env.js`, which
 * sets `window.__ENV__` from `APP_*` environment variables when the container
 * starts. The same image therefore serves dev and prod.
 *
 * Every accessor falls back to `import.meta.env`, so `npm run dev`, vitest and
 * any non-container build keep working from `.env` exactly as before.
 *
 * Nothing read here is secret. All of it reaches the browser either way.
 */

export interface RuntimeEnv {
  API_BASE_URL?: string;
  CLERK_PUBLISHABLE_KEY?: string;
  DEMO_ENABLED?: string;
  SENTRY_DSN?: string;
  /** AN-29: Stripe's publishable key; unset means the hosted Checkout page. */
  STRIPE_PUBLISHABLE_KEY?: string;
  SENTRY_TRACES_SAMPLE_RATE?: string;
  ENVIRONMENT?: string;
}

declare global {
  interface Window {
    __ENV__?: RuntimeEnv;
  }
}

function fromWindow(key: keyof RuntimeEnv): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = window.__ENV__?.[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

function fromVite(key: string): string | undefined {
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

/** Prefer the container-injected value, then the build-time Vite value. */
export function readRuntimeEnv(key: keyof RuntimeEnv): string | undefined {
  return fromWindow(key) ?? fromVite(`VITE_${key}`);
}

export const runtimeEnv = {
  apiBaseUrl: (): string | undefined => readRuntimeEnv("API_BASE_URL"),
  clerkPublishableKey: (): string | undefined => readRuntimeEnv("CLERK_PUBLISHABLE_KEY"),
  demoEnabled: (): boolean => (readRuntimeEnv("DEMO_ENABLED") ?? "").toLowerCase() === "true",
  sentryDsn: (): string | undefined => readRuntimeEnv("SENTRY_DSN"),
  stripePublishableKey: (): string | undefined => readRuntimeEnv("STRIPE_PUBLISHABLE_KEY"),
  sentryTracesSampleRate: (): number =>
    parseFloat(readRuntimeEnv("SENTRY_TRACES_SAMPLE_RATE") ?? "0.1"),
  /** Deployment name for error reporting; falls back to Vite's build mode. */
  environment: (): string => readRuntimeEnv("ENVIRONMENT") ?? import.meta.env.MODE,
};
